import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CalendarDays } from 'lucide-react';
import SEO from '@/components/SEO';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const describeCastingError = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    if (detail.trim().toLowerCase() === 'not found') {
      return 'Casting Director is not fully available on the backend right now. Please redeploy the backend or try again in a few minutes.';
    }
    return detail;
  }
  if (status === 404) {
    return 'Casting Director route was not found on the backend. Please check the latest backend deploy and try again.';
  }
  if (status === 400) {
    return 'KalaManch could not understand this brief yet. Please add a little more detail about art form, city, budget, or event type.';
  }
  if (status >= 500) {
    return 'KalaManch could not create matches right now because the server had a problem. Please try again shortly.';
  }
  return 'Unable to create this brief right now.';
};

const CastingPost = () => {
  const navigate = useNavigate();
  const { artist, isAuthenticated: artistLoggedIn } = useArtistAuth();
  const { collaborator, isAuthenticated: collaboratorLoggedIn } = useCollaboratorAuth();
  const [rawText, setRawText] = useState('');
  const [location, setLocation] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [topN, setTopN] = useState(5);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const activeClientId = collaboratorLoggedIn ? collaborator?.id : artistLoggedIn ? artist?.id : '';
  const activeClientName = collaboratorLoggedIn
    ? collaborator?.organization_name || collaborator?.name
    : artistLoggedIn
      ? artist?.name
      : '';

  const submitBrief = async (event) => {
    event.preventDefault();
    if (!rawText.trim()) {
      setMessage('Please describe the work requirement first.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const briefResponse = await axios.post(`${API}/casting/brief`, {
        raw_text: rawText,
        location,
        budget_range: budgetRange,
        event_date: eventDate,
        top_n: topN,
        client_id: activeClientId,
        client_name: activeClientName,
      });
      const briefId = briefResponse.data?.brief_id;
      if (!briefId) {
        throw new Error('Casting brief was created without an ID. Please refresh and try again.');
      }
      await axios.post(`${API}/casting/match/${briefId}`);
      navigate(`/casting/results/${briefId}`);
    } catch (error) {
      setMessage(describeCastingError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-slate-50 px-4 py-10 md:px-8">
      <SEO
        title="AI Casting Director"
        description="Post a client brief on KalaManch and get AI-ranked artist matches with specific reasons."
        keywords="KalaManch AI casting director, hire artists, artist matching India, event organizer artist search"
      />
      <section className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-[28px] border border-orange-100 bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-orange-500">AI Casting Director</p>
          <h1 className="mt-4 text-4xl font-black text-slate-950 md:text-5xl">Post one brief. Let KalaManch find the fit.</h1>
          <p className="mt-4 text-lg leading-8 text-slate-600">
            Tell us the event, artist type, city, budget, and vibe in plain words. KalaManch reads the brief, ranks matching artists,
            and returns result cards with a clear reason for each match.
          </p>
          <div className="mt-8 space-y-4 rounded-[24px] bg-orange-50 p-6 text-sm leading-7 text-slate-700">
            <div>
              <p className="font-semibold text-slate-950">Works best for:</p>
              <p>events, launches, weddings, college shows, devotional programs, brand shoots, cultural festivals, and workshops</p>
            </div>
            <div>
              <p className="font-semibold text-slate-950">What artists receive:</p>
              <p>match alerts, a reason they were selected, and one-tap interest response inside their KalaManch flow</p>
            </div>
          </div>
        </div>

        <form onSubmit={submitBrief} className="rounded-[28px] border border-orange-100 bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-violet-700">Client Brief</p>
          <h2 className="mt-4 text-3xl font-black text-slate-950">Describe the opportunity</h2>
          <textarea
            value={rawText}
            onChange={(event) => setRawText(event.target.value)}
            rows={8}
            placeholder="Example: Need a soulful Sufi singer and tabla artist for a premium evening event in Delhi NCR for 250 guests. Budget around ₹40,000. Prefer artists with stage presence and devotional / folk crossover."
            className="mt-6 w-full rounded-[24px] border border-slate-200 px-5 py-4 text-base text-slate-900 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <input
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              placeholder="City / state"
              className="rounded-[18px] border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
            <input
              value={budgetRange}
              onChange={(event) => setBudgetRange(event.target.value)}
              placeholder="Budget range"
              className="rounded-[18px] border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
            <label className="relative block">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <CalendarDays className="h-5 w-5" aria-hidden="true" />
              </span>
              <input
                type="date"
                value={eventDate}
                onChange={(event) => setEventDate(event.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full rounded-[18px] border border-slate-200 px-12 py-3 text-slate-900 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </label>
            <select
              value={topN}
              onChange={(event) => setTopN(Number(event.target.value))}
              className="rounded-[18px] border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            >
              <option value={3}>Top 3 matches</option>
              <option value={5}>Top 5 matches</option>
              <option value={8}>Top 8 matches</option>
            </select>
          </div>
          {message ? <p className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">{message}</p> : null}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={loading}
              className="rounded-full bg-orange-500 px-7 py-3 text-base font-semibold text-white shadow-[0_16px_40px_rgba(249,115,22,0.25)] transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Matching artists...' : 'Find matches'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/artists')}
              className="rounded-full border border-orange-300 px-7 py-3 text-base font-semibold text-orange-600 transition hover:bg-orange-50"
            >
              Browse artists
            </button>
          </div>
        </form>
      </section>
    </main>
  );
};

export default CastingPost;
